// The single Sentry integration this app adds on top of the SDK defaults.
//
// It runs *before* `Dedupe` (see `initSentry`), which is what makes the whole
// thing work: until an API failure is renamed, every AxiosError in the app shares
// the type/message pair `AxiosError: Request failed with status code 404`, so
// Dedupe would collapse failures of unrelated endpoints into one another. Renaming
// first gives Dedupe distinguishable events and leaves it doing its real job —
// dropping the same failure reported twice.

import type * as Sentry from '@sentry/react';
import type { Event, EventHint } from '@sentry/react';

import {
  type ApiErrorDescription,
  asAxiosError,
  describeApiError,
  isSuppressedApiError,
} from './apiError.js';
import { getClientContext } from './clientContext.js';
import { MAX_TAG_VALUE_LENGTH, sanitizeEvent, truncate } from './sanitize.js';

export const SPAZIOUS_CONTEXT_INTEGRATION_NAME = 'SpaziousContext';

// Marks an outcome the backend answered on purpose. The Sentry alert rules filter
// on `level:error`, so this tag is what keeps a demoted outcome queryable.
export const API_EXPECTED_TAG = 'api.expected';

// `@sentry/react` re-exports the SDK's public surface but not the `Integration`
// type itself (it lives in `@sentry/types`, which is not a direct dependency), so
// borrow it from a built-in integration factory.
type SentryIntegration = ReturnType<typeof Sentry.dedupeIntegration>;

/** Whether this event was tagged as an expected API outcome by the integration. */
export const isExpectedApiOutcomeEvent = (event: Event): boolean =>
  event.tags?.[API_EXPECTED_TAG] === 'true';

/**
 * Rename and fingerprint an API failure.
 *
 * The issue title becomes `<outcome> <METHOD> <canonical-route>`
 * (`409 PATCH /api/v3/backoffice/orders/{id}/`) by rewriting the exception type,
 * which is what Sentry titles an issue with. Grouping is pinned to exactly those
 * facts, so the same failure with a different record id, query value or set of
 * backend codes groups together while a different route or outcome stays separate.
 *
 * An outcome the backend answered on purpose is demoted to `warning` and tagged,
 * which is what takes it out of the alert rules and out of the error-triggered
 * replay without moving it out of its issue.
 */
const applyApiErrorDescription = (event: Event, description: ApiErrorDescription): void => {
  const { method, route, outcome, codes, errors, expected } = description;

  // Method, route and outcome only. Backend codes are deliberately *not* part of
  // grouping identity: one endpoint answering 422 with `required`, then `invalid`,
  // then both at once would otherwise become three issues sharing one title, and
  // every newly surfaced code would open another issue for an endpoint already
  // known to be failing. They stay searchable on `api.code` and readable in
  // `contexts.api.errors` instead, which is where triage needs them.
  event.fingerprint = ['api', method, route, outcome];

  // Only the level moves: an endpoint's expected outcomes stay in the issue its
  // failures already have, so triage sees a severity change instead of a split.
  if (expected) {
    event.level = 'warning';
  }

  event.tags = {
    ...event.tags,
    'api.method': method,
    'api.route': route,
    // The outcome: an HTTP status when the server answered, the axios transport
    // code (`ERR_NETWORK`, `ECONNABORTED`) when it never did.
    'api.status': outcome,
    // Searchable facet for the codes that came back; a pathologically long list is
    // cut to the tag limit.
    ...(codes.length ? { 'api.code': truncate(codes.join(','), MAX_TAG_VALUE_LENGTH) } : {}),
    ...(expected ? { [API_EXPECTED_TAG]: 'true' } : {}),
  };

  event.contexts = {
    ...event.contexts,
    api: { method, route, outcome, errors },
  };

  // `values` is ordered cause-first, so the error that was actually thrown is last.
  const values = event.exception?.values;
  const primaryException = values?.[values.length - 1];
  if (!primaryException) {
    return;
  }

  // `value` was already scrubbed by `sanitizeEvent`, which runs first.
  primaryException.type = `${outcome} ${method} ${route}`;
  const message = primaryException.value ?? '';
  primaryException.value = codes.length ? `${message} [${codes.join(', ')}]` : message;
};

/**
 * Names and groups API failures, attaches client/device context, and scrubs every
 * outgoing event. Must be installed before `Dedupe`.
 */
export const spaziousContextIntegration = (): SentryIntegration => ({
  name: SPAZIOUS_CONTEXT_INTEGRATION_NAME,

  processEvent(event: Event, hint: EventHint): Event | null {
    const apiError = asAxiosError(hint?.originalException);

    if (apiError && isSuppressedApiError(apiError)) {
      return null;
    }

    sanitizeEvent(event);

    const { tags, context } = getClientContext();
    event.tags = { ...event.tags, ...tags };
    event.contexts = { ...event.contexts, client: context };

    if (apiError) {
      applyApiErrorDescription(event, describeApiError(apiError));
    }

    return event;
  },
});
